/**
 * Create Clinic Modal - Admin-only clinic provisioning interface
 * Calls the provisionClinic Cloud Function to create isolated clinic environments
 */

import React, { useState } from 'react';
import { FiX, FiLoader, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import toast from 'react-hot-toast';

const CreateClinicModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    clinicName: '',
    adminName: '',
    adminEmail: '',
    timezone: 'Asia/Singapore',
    plan: 'starter',
    clinicSlug: ''
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form'); // 'form', 'provisioning', 'success', 'error'
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Auto-generate clinic slug from clinic name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-generate slug when clinic name changes
      if (name === 'clinicName') {
        updated.clinicSlug = generateSlug(value);
      }
      
      return updated;
    });
  };

  const validateForm = () => {
    const { clinicName, adminName, adminEmail, clinicSlug } = formData;
    
    if (!clinicName.trim()) {
      toast.error('Clinic name is required');
      return false;
    }
    
    if (!adminName.trim()) {
      toast.error('Admin name is required');
      return false;
    }
    
    if (!adminEmail.trim()) {
      toast.error('Admin email is required');
      return false;
    }
    
    if (!/\S+@\S+\.\S+/.test(adminEmail)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    
    if (!clinicSlug.trim()) {
      toast.error('Clinic slug is required');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setStep('provisioning');
    setError(null);

    try {
      // Call the provisionClinic Cloud Function
      const provisionClinic = httpsCallable(functions, 'provisionClinic');
      
      console.log('🚀 Starting clinic provisioning...');
      console.log('📋 Form data:', formData);
      console.log('📧 Admin email for welcome email:', formData.adminEmail);
      
      const result = await provisionClinic(formData);
      
      console.log('✅ Clinic provisioning completed successfully!');
      console.log('📊 Result data:', result.data);
      
      if (result.data.clinicId) {
        console.log('🏥 Clinic ID created:', result.data.clinicId);
      }
      
      if (result.data.emailSent !== undefined) {
        console.log('📧 Email status:', result.data.emailSent ? 'SENT' : 'FAILED');
      }
      
      setResult(result.data);
      setStep('success');
      
      toast.success(`Clinic "${formData.clinicName}" created successfully! Welcome email sent to ${formData.adminEmail}`);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(result.data);
      }
      
    } catch (error) {
      console.error('❌ Error provisioning clinic:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        details: error.details
      });
      
      setError(error.message || 'Failed to create clinic');
      setStep('error');
      toast.error('Failed to create clinic: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setStep('form');
      setFormData({
        clinicName: '',
        adminName: '',
        adminEmail: '',
        timezone: 'Asia/Singapore',
        plan: 'starter',
        clinicSlug: ''
      });
      setResult(null);
      setError(null);
      onClose();
    }
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Clinic Name *
          </label>
          <input
            type="text"
            name="clinicName"
            value={formData.clinicName}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., Heale Rehab Clinic"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Clinic Slug *
          </label>
          <input
            type="text"
            name="clinicSlug"
            value={formData.clinicSlug}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="my-clinic-name"
            required
          />
          <p className="text-xs text-gray-500 mt-1">Used for URLs and identification</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Admin Name *
          </label>
          <input
            type="text"
            name="adminName"
            value={formData.adminName}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="John Smith"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Admin Email *
          </label>
          <input
            type="email"
            name="adminEmail"
            value={formData.adminEmail}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="admin@healeclinic.com"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timezone
          </label>
          <select
            name="timezone"
            value={formData.timezone}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="Asia/Singapore">Asia/Singapore</option>
            <option value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur</option>
            <option value="Asia/Bangkok">Asia/Bangkok</option>
            <option value="Asia/Jakarta">Asia/Jakarta</option>
            <option value="Australia/Sydney">Australia/Sydney</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Plan
          </label>
          <select
            name="plan"
            value={formData.plan}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end space-x-4 pt-6 border-t">
        <button
          type="button"
          onClick={handleClose}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          disabled={loading}
        >
          {loading ? (
            <>
              <FiLoader className="animate-spin" />
              <span>Creating Clinic...</span>
            </>
          ) : (
            <span>Create Clinic</span>
          )}
        </button>
      </div>
    </form>
  );

  const renderProvisioning = () => (
    <div className="text-center py-12">
      <FiLoader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Creating Clinic Environment
      </h3>
      <p className="text-gray-600 mb-4">
        Setting up secure multi-tenant environment for {formData.clinicName}...
      </p>
      <div className="text-sm text-gray-500 space-y-1">
        <p>• Creating clinic database structure</p>
        <p>• Setting up admin user and permissions</p>
        <p>• Initializing storage folders</p>
        <p>• Generating welcome materials</p>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center py-12">
      <FiCheck className="w-12 h-12 text-green-600 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Clinic Created Successfully!
      </h3>
      <p className="text-gray-600 mb-6">
        {formData.clinicName} has been provisioned and is ready for use.
      </p>
      
      {result && (
        <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2 mb-6">
          <p><strong>Clinic ID:</strong> {result.clinicId}</p>
          <p><strong>Admin User:</strong> {result.adminUid}</p>
          <p><strong>Login URL:</strong> <a href={result.loginUrl} className="text-blue-600 hover:underline">{result.loginUrl}</a></p>
        </div>
      )}
      
      <button
        onClick={handleClose}
        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Close
      </button>
    </div>
  );

  const renderError = () => (
    <div className="text-center py-12">
      <FiAlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Clinic Creation Failed
      </h3>
      <p className="text-gray-600 mb-4">
        There was an error creating the clinic environment.
      </p>
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left mb-6">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}
      
      <div className="flex justify-center space-x-4">
        <button
          onClick={() => setStep('form')}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Try Again
        </button>
        <button
          onClick={handleClose}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Create New Clinic
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6">
          {step === 'form' && renderForm()}
          {step === 'provisioning' && renderProvisioning()}
          {step === 'success' && renderSuccess()}
          {step === 'error' && renderError()}
        </div>
      </div>
    </div>
  );
};

export default CreateClinicModal;
